import React, { useState } from 'react';

import {
  Alert,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { CompetitorProperty, CompetitorPropertyOption } from '~/types/services/priceParrot';

import { useCompetitorOfferOptions } from './useCompetitorOfferOptions';
import {
  cancellationText,
  formatAmountWithAud,
  formatStayPrice,
  plural,
  stayBasis,
  stayOf,
  taxBasisLabel,
} from './utils';

const COMPARED_FIELDS: Array<{ label: string; value: (option: CompetitorPropertyOption) => string }> = [
  { label: 'Stay price', value: (option) => formatStayPrice(stayOf(option)) },
  { label: 'Stay', value: (option) => stayBasis(option.stayNights, option.adults, option.stayCheckIn) },
  {
    label: 'Per night',
    value: (option) =>
      `${formatAmountWithAud(option.pricePerNight, option.pricePerNightCurrency, option.pricePerNightAud)} (${taxBasisLabel(option.pricePerNightIncludesTax)})`,
  },
  { label: 'Min stay', value: (option) => (option.minNights ? plural(option.minNights, 'night') : '—') },
  {
    label: 'Room capacity',
    value: (option) =>
      option.maxAdults
        ? plural(option.maxAdults, 'adult')
        : option.maxGuests
          ? plural(option.maxGuests, 'guest')
          : 'Not collected',
  },
  { label: 'Cancellation', value: cancellationText },
  { label: 'Inclusions', value: (option) => option.inclusions ?? '—' },
];

interface Props {
  property: CompetitorProperty;
  /** A past collection to show instead of the current deal. */
  collectionId?: string;
}

export default function CompetitorOfferPackageComparison({ property, collectionId }: Props) {
  const [firstIndex, setFirstIndex] = useState(0);
  const [secondIndex, setSecondIndex] = useState(1);
  const { options, isLoading, error } = useCompetitorOfferOptions(property, collectionId);

  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (error) {
    return <Alert severity="error">Failed to load room options: {error.message}</Alert>;
  }
  if (options.length < 2) {
    return <Typography color="text.secondary">This deal has fewer than two room options to compare.</Typography>;
  }

  const first = options[Math.min(firstIndex, options.length - 1)];
  const second = options[Math.min(secondIndex, options.length - 1)];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {[
          { label: 'First option', value: firstIndex, onChange: setFirstIndex },
          { label: 'Second option', value: secondIndex, onChange: setSecondIndex },
        ].map((picker) => (
          <TextField
            key={picker.label}
            select
            size="small"
            label={picker.label}
            value={Math.min(picker.value, options.length - 1)}
            onChange={(event) => picker.onChange(Number(event.target.value))}
            sx={{ flex: 1 }}
          >
            {options.map((option, index) => (
              <MenuItem key={option.id} value={index}>
                {option.name ?? option.id}
              </MenuItem>
            ))}
          </TextField>
        ))}
      </Stack>
      <Table size="small">
        <TableBody>
          {COMPARED_FIELDS.map((field) => {
            const firstValue = field.value(first);
            const secondValue = field.value(second);
            const isDifferent = firstValue !== secondValue;
            return (
              <TableRow key={field.label}>
                <TableCell sx={{ width: 160, color: 'text.secondary' }}>{field.label}</TableCell>
                {[firstValue, secondValue].map((value, index) => (
                  <TableCell key={index} sx={{ fontWeight: isDifferent ? 'medium' : undefined, width: '42%' }}>
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
}
